import { useEffect, useRef } from 'react';

// Global image cache that persists across component remounts
const globalImageCache = {};

// List of all images to preload
const IMAGES_TO_PRELOAD = [
  '/josh_le.JPG',
  '/cat_dad.jpeg',
  '/latte_artist.jpeg',
  '/new_york.jpeg',
  '/san_francisco.jpeg'
];

// Preload all images immediately when module loads
IMAGES_TO_PRELOAD.forEach((src) => {
  if (!globalImageCache[src]) {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = src;
    img.onload = () => {
      console.log(`Image preloaded: ${src}`);
    };
    img.onerror = () => {
      console.error(`Failed to preload image: ${src}`);
    };
    globalImageCache[src] = img;
  }
});

const ShoreWaves = ({ hoverState }) => {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const transitionRef = useRef({
    targetImage: null,
    isActive: false
  });
  const timeRef = useRef(0);
  const hoverTimeRef = useRef(0);
  const imageOpacityRef = useRef(0);
  const maxHoverTimeReachedRef = useRef(0); // Track the peak hoverTime for smooth return
  const lastHoverStateRef = useRef({ active: false, image: null });

  // Ensure images are loaded (in case module-level preload didn't complete)
  useEffect(() => {
    IMAGES_TO_PRELOAD.forEach((src) => {
      if (!globalImageCache[src]) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = src;
        globalImageCache[src] = img;
      }
    });
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    // High-DPI canvas setup for crisp rendering
    const displayWidth = 550;
    const displayHeight = 550;
    const dpr = window.devicePixelRatio || 1;
    
    // Set the actual canvas size in memory (scaled up for high-DPI)
    canvas.width = displayWidth * dpr;
    canvas.height = displayHeight * dpr;
    
    // Scale the canvas CSS size back down to the display size
    canvas.style.width = displayWidth + 'px';
    canvas.style.height = displayHeight + 'px';
    
    // Scale the canvas context to ensure correct drawing operations
    ctx.scale(dpr, dpr);
    
    // Improve rendering quality
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    const animate = () => {
      // Handle hover state changes
      if (hoverState.active !== lastHoverStateRef.current.active || 
          hoverState.image !== lastHoverStateRef.current.image) {
        
        console.log('Hover state changed:', hoverState);
        
        if (hoverState.active && hoverState.image) {
          transitionRef.current.targetImage = hoverState.image;
          transitionRef.current.isActive = true;
          hoverTimeRef.current = 0; // Reset hover time
          console.log('Starting hover animation for:', hoverState.image);
        } else {
          transitionRef.current.isActive = false;
          console.log('Ending hover animation');
        }
        lastHoverStateRef.current = { ...hoverState };
      }
      
      // Clear canvas
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, displayWidth, displayHeight);
      
      // Calculate wave expansion progress for image fade-in/out
      const maxDistance = Math.sqrt((displayWidth/2) ** 2 + (displayHeight/2) ** 2);
      
      if (hoverState.active && transitionRef.current.targetImage) {
        // Track max hover time reached for smooth return animation
        maxHoverTimeReachedRef.current = Math.max(maxHoverTimeReachedRef.current, hoverTimeRef.current);
        
        // Calculate how much of the screen has been cleared by the wave
        const expansionWave = hoverTimeRef.current * 80; // Same speed as wave expansion
        const expansionProgress = Math.min(expansionWave / maxDistance, 1);
        
        // Image fades in as the wave expands and clears the screen
        const targetOpacity = Math.pow(expansionProgress, 1.5) * 0.95;
        imageOpacityRef.current = targetOpacity;
      } else if (transitionRef.current.targetImage && imageOpacityRef.current > 0) {
        // Fade out image SLOWER than lines return - keep image visible longer
        // Use a delayed fade that starts after lines begin returning
        const returnProgress = hoverTimeRef.current / Math.max(maxHoverTimeReachedRef.current, 0.1);
        
        // Delay the image fade - only start fading when return is 40% complete
        const delayedProgress = Math.max(0, (returnProgress - 0.1) / 0.9);
        imageOpacityRef.current = Math.pow(delayedProgress, 1.2) * 0.95;
        
        // Clear the target image once fully faded out
        if (imageOpacityRef.current < 0.01 && hoverTimeRef.current === 0) {
          imageOpacityRef.current = 0;
          maxHoverTimeReachedRef.current = 0;
        }
      }
      
      const imageOpacity = imageOpacityRef.current;
      
      // Draw background image with synchronized opacity
      if (imageOpacity > 0.01 && transitionRef.current.targetImage) {
        const img = globalImageCache[transitionRef.current.targetImage];
        if (img && img.complete && img.naturalWidth > 0) {
          ctx.save();
          ctx.globalAlpha = imageOpacity;
          
          // Improve color reproduction
          ctx.globalCompositeOperation = 'source-over';
          
          // Calculate image dimensions to cover the canvas
          const canvasAspect = displayWidth / displayHeight;
          const imgAspect = img.naturalWidth / img.naturalHeight;
          
          let drawWidth, drawHeight, offsetX, offsetY;
          
          if (imgAspect > canvasAspect) {
            drawHeight = displayHeight;
            drawWidth = drawHeight * imgAspect;
            offsetX = (displayWidth - drawWidth) / 2;
            offsetY = 0;
          } else {
            drawWidth = displayWidth;
            drawHeight = drawWidth / imgAspect;
            offsetX = 0;
            offsetY = (displayHeight - drawHeight) / 2;
          }
          
          ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
          ctx.restore();
        }
      }
      
      // Set text properties for ASCII art with improved quality
      ctx.font = '10px monospace';
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'center';
      // Better text rendering
      ctx.textRenderingOptimization = 'optimizeQuality';
      
      // Create flowing wave patterns
      const rows = 55;
      const cols = 70;
      const cellWidth = displayWidth / cols;
      const cellHeight = displayHeight / rows;
      
      // Track hover time for wave expansion
      const isReturning = !hoverState.active && hoverTimeRef.current > 0;
      
      if (hoverState.active) {
        hoverTimeRef.current += 0.04;
        maxHoverTimeReachedRef.current = Math.max(maxHoverTimeReachedRef.current, hoverTimeRef.current);
      } else {
        // Return animation starts immediately and moves faster for snappier feel
        hoverTimeRef.current = Math.max(0, hoverTimeRef.current - 0.06);
      }
      
      const hoverTime = hoverTimeRef.current;
      const maxHoverTime = maxHoverTimeReachedRef.current;
      const time = timeRef.current;
      
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const x = col * cellWidth;
          const y = row * cellHeight;
          
          // Distance from center for expansion effect
          const centerX = displayWidth / 2;
          const centerY = displayHeight / 2;
          const distanceFromCenter = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);
          
          // Create pseudo-random scatter based on position (deterministic so it's consistent)
          const scatter = Math.sin(x * 0.1 + y * 0.13) * 0.5 + 
                         Math.cos(x * 0.07 - y * 0.11) * 0.5;
          const scatterOffset = scatter * 40; // Scatter amount in pixels
          
          // Wave influence with expansion
          let waveInfluence;
          let characterOpacity = 1;
          
          if (hoverTime > 0) {
            if (hoverState.active) {
              // EXPANDING: Vertical sweep from TOP to BOTTOM
              const expansionProgress = hoverTime / 5; // Adjust speed (higher = slower)
              const expansionWavePosition = expansionProgress * (displayHeight + 100);
              const adjustedY = y + scatterOffset * 0.5; // Less scatter for cleaner vertical sweep
              
              // Characters disappear when the vertical sweep reaches them
              if (adjustedY < expansionWavePosition) {
                // Character is being swept away
                const sweepDistance = expansionWavePosition - adjustedY;
                const fadeOutZone = 60; // Fade zone for smooth sweep
                characterOpacity = Math.max(0, 1 - sweepDistance / fadeOutZone);
                // Add subtle scatter to opacity for organic feel
                characterOpacity *= (0.7 + Math.abs(scatter) * 0.3);
              }
              // Characters below the wave are still fully visible (opacity = 1)
            } else {
              // RETURNING: Vertical sweep from BOTTOM to TOP
              const returnProgress = 1 - (hoverTime / maxHoverTime);
              const returnWavePosition = displayHeight - (returnProgress * (displayHeight + 100));
              const adjustedY = y + scatterOffset * 0.5;
              
              // Characters appear when the vertical sweep reaches them (from bottom)
              if (adjustedY > returnWavePosition) {
                // Character is returning - fade in gradually
                const returnDistance = adjustedY - returnWavePosition;
                const fadeInZone = 60; // Fade zone for smoother sweep
                characterOpacity = Math.min(1, returnDistance / fadeInZone);
                // Add subtle scatter to opacity for organic feel
                characterOpacity *= (0.7 + Math.abs(scatter) * 0.3);
              } else {
                // Character hasn't returned yet
                characterOpacity = 0;
              }
            }
            
            // Horizontal wave pattern during transitions (flows with vertical sweep)
            const waveSpeed = 1.5;
            const horizontalWave = Math.sin(x * 0.015 - time * waveSpeed) * 2;
            const normalWave = Math.sin(x * 0.01 + time * 0.5) * 0.5 + 
                              Math.sin(x * 0.02 - time * 0.3) * 0.3;
            
            // Blend between horizontal and normal wave based on transition progress
            const transitionBlend = isReturning ? 1 - (hoverTime / maxHoverTime) : hoverTime / 5;
            const blendFactor = Math.min(1, transitionBlend);
            waveInfluence = horizontalWave * (1 - blendFactor) + normalWave * blendFactor + 
                           Math.sin(x * 0.01 + time * 0.5) * 0.3;
          } else {
            // Normal wave pattern - gentle shore waves
            waveInfluence = Math.sin(x * 0.01 + time * 0.5) * 0.5 + 
                           Math.sin(x * 0.02 - time * 0.3) * 0.3;
          }
          
          // Skip drawing if character is swept away
          if (characterOpacity < 0.01) continue;
          
          // Shore waves pulse - smoothly blend between horizontal and shore patterns
          const shoreDistance = Math.abs(y - displayHeight * 0.5);
          const horizontalPulse = Math.sin(time * 1.5 - x * 0.01) * 60;
          const normalPulse = Math.sin(time * 0.8 - shoreDistance * 0.02) * 50;
          
          let shorePulse;
          if (hoverTime > 0) {
            if (isReturning && maxHoverTime > 0) {
              // Blend from horizontal back to normal shore pulse
              const blendFactor = hoverTime / maxHoverTime;
              shorePulse = horizontalPulse * blendFactor + normalPulse * (1 - blendFactor);
            } else {
              // During expansion, use horizontal pulse
              const expansionBlend = Math.min(1, hoverTime / 3);
              shorePulse = normalPulse * (1 - expansionBlend) + horizontalPulse * expansionBlend;
            }
          } else {
            shorePulse = normalPulse;
          }
          
          // Calculate flow direction
          const flowAngle = Math.atan2(
            Math.sin(y * 0.02 + time * 0.6 + waveInfluence) * 30,
            Math.cos(x * 0.015 + time * 0.4) * 40 + shorePulse
          );
          
          // Add turbulence - smoothly transition back to normal during return
          let turbulenceMultiplier = 1;
          if (hoverTime > 0) {
            if (isReturning && maxHoverTime > 0) {
              // Smoothly reduce turbulence as we return
              turbulenceMultiplier = 1 + 2 * (hoverTime / maxHoverTime);
            } else {
              turbulenceMultiplier = 3;
            }
          }
          const turbulence = Math.sin(x * 0.05 + y * 0.05 + time * 2) * 
                           (1 - shoreDistance / (displayHeight * 0.5)) * turbulenceMultiplier;
          
          // Determine character
          let char = '';
          const angle = (flowAngle + turbulence) % (Math.PI * 2);
          
          if (angle < Math.PI / 4 || angle > 7 * Math.PI / 4) {
            char = "'";
          } else if (angle < 3 * Math.PI / 4) {
            char = "/";
          } else if (angle < 5 * Math.PI / 4) {
            char = ",";
          } else {
            char = "\\";
          }
          
          // Foam effect
          const waveHeight = Math.sin(x * 0.02 + time * 1.2) * 
                           Math.sin(y * 0.03 - time * 0.8);
          if (waveHeight > 0.7 && row > rows * 0.3 && row < rows * 0.7) {
            char = ".";
          }
          
          // Vary opacity based on wave intensity and sweep effect
          const baseIntensity = Math.abs(Math.sin(flowAngle + time * 0.5));
          const baseOpacity = (0.2 + baseIntensity * 0.5) * characterOpacity;
          
          ctx.globalAlpha = baseOpacity;
          
          // Color based on wave proximity (vertical sweeps)
          if (hoverTime > 0) {
            // Expansion wave position (top to bottom)
            const expansionProgress = hoverTime / 5;
            const expansionWavePosition = expansionProgress * (displayHeight + 100);
            const expansionWaveProximity = Math.abs(y - expansionWavePosition);
            
            // Return wave position (bottom to top)
            const returnProgress = 1 - (hoverTime / maxHoverTime);
            const returnWavePosition = displayHeight - (returnProgress * (displayHeight + 100));
            const returnWaveProximity = Math.abs(y - returnWavePosition);
            
            // Green highlight on the active wave front
            if ((hoverState.active && expansionWaveProximity < 40) || 
                (isReturning && returnWaveProximity < 40)) {
              ctx.fillStyle = 'rgba(16, 185, 129, 0.9)'; // Sage green for the wave front
            } else {
              ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
            }
          } else {
            ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
          }
          
          // Draw the character
          ctx.fillText(char, x + cellWidth / 2, y + cellHeight / 2);
        }
      }
      
      // Reset global alpha
      ctx.globalAlpha = 1;
      
      timeRef.current += hoverTimeRef.current > 0 ? 0.025 : 0.015;
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    
    animate();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      
      if (canvas && ctx) {
        ctx.clearRect(0, 0, displayWidth, displayHeight);
      }
    };
  }, [hoverState]);

  return (
    <div style={{ width: '550px', height: '550px', backgroundColor: '#ffffff' }}>
      <canvas ref={canvasRef} />
    </div>
  );
};

export default ShoreWaves;
